import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function OwnerProductEdit() {
  const { productId } = useParams();
  const isNew = !productId;
  const [form, setForm] = useState({
    title: "", subtitle: "", description: "", price: 0, sku: "", manufacturer: "", condition: "OEM",
    ebay_url: "", ebay_item_number: "", main_image: "", stock: 5, published: true, warranty: "", engine_size: "",
    oem_part_numbers_text: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/products?q=${productId}`).then(async () => {
      const { data } = await api.get(`/products/${productId}`).catch(() => ({ data: null }));
      if (data) setForm({
        ...data,
        oem_part_numbers_text: (data.oem_part_numbers || []).join(", "),
      });
    });
  }, [productId, isNew]);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      stock: parseInt(form.stock) || 0,
      oem_part_numbers: (form.oem_part_numbers_text || "").split(",").map((s) => s.trim()).filter(Boolean),
      main_image: form.main_image,
      images: form.main_image ? [form.main_image] : (form.images || []),
    };
    delete payload.oem_part_numbers_text;
    try {
      if (isNew) {
        await api.post("/admin/products", payload);
      } else {
        await api.put(`/admin/products/${productId}`, payload);
      }
      toast.success("Saved.");
      navigate("/owner-cabinet/products");
    } catch (err) { toast.error("Could not save. " + (err?.response?.data?.detail || "")); }
  };

  const del = async () => {
    if (!confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${productId}`);
      toast.success("Deleted.");
      navigate("/owner-cabinet/products");
    } catch (err) { toast.error("Could not delete."); }
  };

  return (
    <div data-testid="owner-product-edit">
      <Link to="/owner-cabinet/products" className="text-slate-400 hover:text-white text-xs font-mono uppercase tracking-widest flex items-center gap-1 mb-4"><ArrowLeft className="w-3 h-3" /> Back to products</Link>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl font-extrabold text-white">{isNew ? "New product" : "Edit product"}</h1>
        <div className="flex items-center gap-2">
          {!isNew && <Button variant="outline" onClick={del} data-testid="admin-delete-product" className="border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>}
          <Button onClick={save} data-testid="admin-save-product" className="bg-[#0055FF] hover:bg-[#0044CC]"><Save className="w-4 h-4 mr-1" /> Save</Button>
        </div>
      </div>

      <form onSubmit={save} className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <section className="rounded-xl border border-white/10 bg-white/5 p-5 space-y-3">
            <div><Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" required data-testid="admin-field-title" /></div>
            <div><Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Subtitle</Label><Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Description</Label><Textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" /></div>
            <div>
              <Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Main image URL</Label>
              <Input value={form.main_image} onChange={(e) => setForm({ ...form, main_image: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" />
              {form.main_image && <img src={form.main_image} alt="" className="mt-3 max-h-48 object-contain bg-white rounded" />}
            </div>
          </section>
        </div>
        <aside className="space-y-4">
          <section className="rounded-xl border border-white/10 bg-white/5 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Published</Label>
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
            </div>
            <div><Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Price</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" data-testid="admin-field-price" /></div>
            <div><Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white font-mono" /></div>
            <div><Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Manufacturer</Label><Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Condition</Label><Input value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Engine size</Label><Input value={form.engine_size || ""} onChange={(e) => setForm({ ...form, engine_size: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Warranty</Label><Input value={form.warranty || ""} onChange={(e) => setForm({ ...form, warranty: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" /></div>
          </section>
          <section className="rounded-xl border border-white/10 bg-white/5 p-5 space-y-3">
            <h3 className="text-[10px] font-mono uppercase tracking-widest text-slate-400">eBay linkage</h3>
            <div><Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">eBay URL</Label><Input value={form.ebay_url} onChange={(e) => setForm({ ...form, ebay_url: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white font-mono" /></div>
            <div><Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Item number</Label><Input value={form.ebay_item_number} onChange={(e) => setForm({ ...form, ebay_item_number: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white font-mono" /></div>
          </section>
          <section className="rounded-xl border border-white/10 bg-white/5 p-5 space-y-3">
            <h3 className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Part numbers</h3>
            <div><Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">OEM (comma-separated)</Label><Textarea rows={3} value={form.oem_part_numbers_text} onChange={(e) => setForm({ ...form, oem_part_numbers_text: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white font-mono" /></div>
          </section>
        </aside>
      </form>
    </div>
  );
}
